import React, { useState, useEffect } from 'react';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // Main style file
import 'react-date-range/dist/theme/default.css'; // Theme CSS file
import './App.css';

function LeaveDashboard() {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [deniedRequests, setDeniedRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Function to get the start and end dates of the current week
  const getCurrentWeekDates = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Start from Sunday
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (6 - today.getDay())); // End on Saturday

    return {
      startDate: startOfWeek,
      endDate: endOfWeek,
    };
  };

  const [dateRange, setDateRange] = useState([
    {
      startDate: getCurrentWeekDates().startDate,
      endDate: getCurrentWeekDates().endDate,
      key: 'selection',
    },
  ]);

  // Function to fetch data based on the selected date range
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://anltcs.noorride.com/leave-req?start_date=${dateRange[0].startDate.toISOString().split('T')[0]}&end_date=${dateRange[0].endDate.toISOString().split('T')[0]}`
      );
      const data = await response.json();
      // Ensure the data is properly initialized
      setLeaveRequests(data.leave_requests || []);
      setApprovedRequests(data.approved_requests || []);
      setDeniedRequests(data.denied_requests || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const updateStatus = async (id, newStatus) => {
    try {
      const response = await fetch(
        `https://anltcs.noorride.com/update-leave-status?id=${id}&status=${newStatus}`,
        {
          method: 'GET', // Your API uses GET
        }
      );

      const result = await response.json();

      if (result === 1) {
        // Successfully updated
        console.log('Status updated successfully');
        fetchData(); // Refetch data to update the UI
      } else if (result === 0) {
        // Failed to update
        console.error('Failed to update status');
        alert('Failed to update status. Please try again.');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('An error occurred while updating the status.');
    }
  };

  const handleStatusChange = (id, newStatus) => {
    // Update the status in the backend
    updateStatus(id, newStatus);

    // Update the local state for immediate UI feedback
    const request = leaveRequests.find((req) => req.id === id);
    if (request) {
      const updatedRequest = { ...request, status: newStatus };
      const newLeaveRequests = leaveRequests.filter((req) => req.id !== id);
      if (newStatus === 'approved') {
        setApprovedRequests([...approvedRequests, updatedRequest]);
      } else if (newStatus === 'denied') {
        setDeniedRequests([...deniedRequests, updatedRequest]);
      }
      setLeaveRequests(newLeaveRequests);
    }
  };

  // Filter leave requests by driver name
  const filteredLeaveRequests = leaveRequests.filter((request) =>
    request.driver_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredApprovedRequests = approvedRequests.filter((request) =>
    request.driver_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDeniedRequests = deniedRequests.filter((request) =>
    request.driver_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="App">
      <h1>Driver Leaves Management</h1>

      {/* Date Range Picker */}
      <div className="date-range-picker">
        <h3>Select Date Range</h3>
        <div
          className="date-range-input"
          onClick={() => setShowDatePicker(!showDatePicker)}
        >
          {`${dateRange[0].startDate.toLocaleDateString()} - ${dateRange[0].endDate.toLocaleDateString()}`}
        </div>
        {showDatePicker && (
          <div className="rdrDateRangePickerWrapper">
            <DateRangePicker
              onChange={(item) => {
                setDateRange([item.selection]);
                setShowDatePicker(false);
              }}
              showSelectionPreview={true}
              moveRangeOnFirstSelection={false}
              months={1}
              ranges={dateRange}
              direction="horizontal"
            />
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by driver name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="loading-spinner">Loading...</div>
      ) : (
        <div className="requests-container">
          {/* Pending Requests Section */}
          <div className="pending-section">
            <h2>Pending Leave Requests</h2>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th>Date</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeaveRequests.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="no-data">
                        No pending requests
                      </td>
                    </tr>
                  ) : (
                    filteredLeaveRequests.map((request) => (
                      <tr key={request.id}>
                        <td>{request.driver_name}</td>
                        <td>{request.date}</td>
                        <td>{request.reason}</td>
                        <td>{request.status}</td>
                        <td>
                          <button
                            className="green-button"
                            onClick={() => handleStatusChange(request.id, 'approved')}
                          >
                            Approve
                          </button>
                          <button
                            className="blue-button"
                            onClick={() => handleStatusChange(request.id, 'week off')}
                          >
                            Week Off
                          </button>
                          <button
                            className="red-button"
                            onClick={() => handleStatusChange(request.id, 'denied')}
                          >
                            Deny
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Approved Requests Section */}
          <div className="approved-section">
            <h2>Approved Requests</h2>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th>Date</th>
                    <th>Reason</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApprovedRequests.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="no-data">
                        No approved requests
                      </td>
                    </tr>
                  ) : (
                    filteredApprovedRequests.map((request) => (
                      <tr key={request.id} className="approved-row">
                        <td>{request.driver_name}</td>
                        <td>{request.date}</td>
                        <td>{request.reason}</td>
                        <td>{request.status}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Denied Requests Section */}
          <div className="denied-section">
            <h2>Denied Requests</h2>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th>Date</th>
                    <th>Reason</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeniedRequests.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="no-data">
                        No denied requests
                      </td>
                    </tr>
                  ) : (
                    filteredDeniedRequests.map((request) => (
                      <tr key={request.id} className="denied-row">
                        <td>{request.driver_name}</td>
                        <td>{request.date}</td>
                        <td>{request.reason}</td>
                        <td>{request.status}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeaveDashboard;